import React, { useEffect, useRef, useState, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { connect } from 'react-redux';
import clsx from 'clsx';
import svgFetcher from 'acq-components/lib/utils/svgFetcher';
import CardUnit from './CardUnit';
import { isIOS, getWinWidth } from 'src/universal/utils/clientSideDetection';
import { sendDTMClickEvent } from 'service_modules/models/dtm-tagging';
import replaceStringWithVars from 'src/universal/utils/replaceStringWithVars';
import { getCardListOrderData } from './redux';
import styles from './__styles__/index.scss?modules';
import { calculateTabIndex } from '../../utils';
import {
  isG174Experiment,
  getG174CardListOrderData
} from 'src/universal/components/Experiments/G174/redux';

const ArrowCta = ({ imgName, clickHandler, arrowType = '', shownArrows }) => {
  const ariaVal = `Scroll ${arrowType}`;
  return (
    <button
      className={styles[`${arrowType}Arrow`]}
      onClick={clickHandler}
      aria-label={ariaVal}
      title={ariaVal}
      tabIndex={calculateTabIndex(styles, shownArrows, arrowType)}
    >
      {svgFetcher(imgName, 'dls') || null}
    </button>
  );
};

const CardListCarousel = ({ bauCardListOrder, g174CardListOrder, isG174 }) => {
  const cardListOrderToUse = isG174 ? g174CardListOrder : bauCardListOrder;

  const { cardList, shouldHide, cardListCarousel } = cardListOrderToUse;
  const listContainerRef = useRef(null);
  const listStripRef = useRef(null);
  const winWidth = getWinWidth();
  const isMobile = winWidth <= 767;

  const getUnitWidth = useCallback(
    () => {
      return isMobile ? 130 : 140;
    },
    [isMobile]
  );

  const getInitialArrowCls = useCallback(
    () => {
      const availableW = winWidth - 100;
      if (availableW > getUnitWidth() * cardList.length) {
        return styles.hideBothArrows;
      }
      return styles.showRightArrowOnly;
    },
    [winWidth, cardList]
  );

  const [showArrowCls, setShowArrowCls] = useState(getInitialArrowCls());

  const calcArrowVisibilities = () => {
    const itemContainerEle = listContainerRef.current;
    let resultClassName = styles.hideBothArrows;

    if (itemContainerEle) {
      const maxScrollLeftVal =
        itemContainerEle.scrollWidth - itemContainerEle.clientWidth;
      const currScrollLeft = itemContainerEle.scrollLeft;
      const scrollLeftAdjusted =
        currScrollLeft > 0 ? currScrollLeft + 15 : currScrollLeft;

      const canFitAll =
        cardList.length * getUnitWidth() < itemContainerEle.clientWidth;

      if (!canFitAll && maxScrollLeftVal >= 0) {
        if (scrollLeftAdjusted >= maxScrollLeftVal) {
          resultClassName = styles.showLeftArrowOnly;
        } else if (scrollLeftAdjusted <= 0) {
          resultClassName = styles.showRightArrowOnly;
        } else {
          resultClassName = styles.showBothArrows;
        }
      }
    }

    setShowArrowCls(resultClassName);
  };

  const debouncedHandler = debounce(calcArrowVisibilities, 500);

  const createPaddleClickHandler = isLeft => {
    const paddleDtm = (cardListCarousel?.dtmEvents || []).find(
      dtm => dtm.key === 'carouselPaddleClick'
    );
    return () => {
      const itemContainerEle = listContainerRef.current;

      if (!itemContainerEle) {
        return;
      }

      const currPosX = itemContainerEle.scrollLeft;
      const incrementX = itemContainerEle.clientWidth;
      const unitWidth = getUnitWidth();
      let nextScrollX = isLeft ? currPosX - incrementX : currPosX + incrementX;

      const unitCnt = Math.round(nextScrollX / unitWidth);
      const offset = isMobile ? 0 : 25;

      if (unitCnt > 0) {
        nextScrollX = unitCnt * unitWidth - offset;
      }

      if (nextScrollX < 0) {
        nextScrollX = 0;
      }

      if (isLeft && nextScrollX < 70) {
        nextScrollX = 0;
      }

      if (typeof itemContainerEle.scrollTo === 'function') {
        itemContainerEle.scrollTo({
          top: itemContainerEle.scrollTop,
          left: nextScrollX,
          behavior: 'smooth'
        });
      } else {
        itemContainerEle.scrollLeft = nextScrollX;
      }

      calcArrowVisibilities();

      setTimeout(() => {
        const dtm = {
          ...paddleDtm,
          eventAsset: replaceStringWithVars({
            varsObject: { direction: isLeft ? 'left' : 'right' },
            string: paddleDtm?.eventAsset
          })
        };
        sendDTMClickEvent(dtm);
      }, 50);
    };
  };

  useEffect(() => {
    const timeout1 = setTimeout(() => {
      const container = listContainerRef.current;
      if (container?.addEventListener) {
        container.addEventListener('scroll', debouncedHandler);
      }
    }, isIOS() ? 2000 : 500);

    return () => {
      const container = listContainerRef.current;
      if (container?.removeEventListener) {
        container.removeEventListener('scroll', debouncedHandler);
      }
      clearTimeout(timeout1);
    };
  }, []);

  useEffect(
    () => {
      const timeout2 = setTimeout(() => {
        calcArrowVisibilities();
      }, isIOS() ? 3000 : 1200);

      return () => {
        clearTimeout(timeout2);
      };
    },
    [cardList]
  );

  return (
    <div className={clsx(styles.outerBox, { [styles.shouldHide]: shouldHide })}>
      <div className={styles.divider} />
      <div ref={listStripRef} className={clsx(styles.cardListStrip, showArrowCls)}>
        <ArrowCta
          imgName="dls-icon-left"
          clickHandler={createPaddleClickHandler(true)}
          arrowType="left"
          shownArrows={showArrowCls}
        />
        <div className={styles.cardListViewPort}>
          <div ref={listContainerRef} className={styles.cardListContainer}>
            {cardList.map((item, index) => {
              return <CardUnit key={`k${index}`} {...item} />;
            })}
          </div>
        </div>
        <ArrowCta
          imgName="dls-icon-right"
          clickHandler={createPaddleClickHandler(false)}
          arrowType="right"
          shownArrows={showArrowCls}
        />
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  return {
    bauCardListOrder: getCardListOrderData(state),
    g174CardListOrder: getG174CardListOrderData(state),
    isG174: isG174Experiment(state)
  };
};

export default connect(mapStateToProps)(React.memo(CardListCarousel));
